import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { agenciesApi, workspacesApi, ApiError } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { Button } from '@/components/ui/Button';

/**
 * First-run setup shown on the Dashboard while no workspace is selected.
 * A brand-new account has no agency yet (sign-up creates only the user), so it
 * walks through "create agency" first and "create workspace" second, offering
 * each action inline instead of pointing at the sidebar switcher.
 */
export function FirstRunSetup() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const { currentAgency, setCurrentAgency, setCurrentWorkspace } = useAppStore();
  const [name, setName] = useState('');

  // Shares the switcher's cache; useScopeAutoSelect (mounted in Shell) selects
  // the first agency/workspace once loaded.
  const { data: agenciesData, isLoading, isError, refetch } = useQuery({
    queryKey: ['agencies'],
    queryFn: async () => {
      const token = await getToken();
      return agenciesApi.list(token!);
    },
  });

  const { data: workspacesData, isLoading: workspacesLoading } = useQuery({
    queryKey: ['workspaces', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return workspacesApi.list(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  const create = useMutation({
    mutationFn: async (value: string) => {
      const token = await getToken();
      if (!currentAgency) {
        const { agency } = await agenciesApi.create(token!, value);
        return { agency, workspace: null };
      }
      const { workspace } = await workspacesApi.create(token!, {
        agency_id: currentAgency.id,
        name: value,
      });
      return { agency: null, workspace };
    },
    onSuccess: ({ agency, workspace }) => {
      if (agency) {
        qc.invalidateQueries({ queryKey: ['agencies'] });
        setCurrentAgency(agency);
      }
      if (workspace) {
        qc.invalidateQueries({ queryKey: ['workspaces', currentAgency?.id] });
        setCurrentWorkspace(workspace);
      }
      setName('');
    },
  });

  const hasAgencies = (agenciesData?.agencies.length ?? 0) > 0;
  const hasWorkspaces = (workspacesData?.workspaces.length ?? 0) > 0;

  // Without the agency list we can't tell a new account from an existing one —
  // offering "Create agency" here could create a duplicate.
  if (isError) {
    return (
      <p className="mt-4 text-xs text-destructive">
        Couldn't load your agencies.{' '}
        <button type="button" onClick={() => refetch()} className="underline">
          Retry
        </button>
      </p>
    );
  }

  // An agency or workspace exists but hasn't been selected yet: wait for it.
  if (
    isLoading ||
    (!currentAgency && hasAgencies) ||
    (!!currentAgency && (workspacesLoading || hasWorkspaces))
  ) {
    return <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-muted-foreground" />;
  }

  const step = currentAgency ? 'workspace' : 'agency';
  // Only owners/admins can create workspaces; an invited editor/viewer with no
  // brand assigned yet must wait for an admin instead of hitting a 403.
  const myRole =
    agenciesData?.agencies.find((a) => a.id === currentAgency?.id)?.role ?? currentAgency?.role;
  const canCreateWorkspace = !currentAgency || !myRole || ['owner', 'admin'].includes(myRole);
  if (step === 'workspace' && !canCreateWorkspace) {
    return (
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground text-pretty">
        You have joined {currentAgency?.name}. Ask an agency admin to give you access to a brand.
      </p>
    );
  }
  const submit = () => {
    if (name.trim()) create.mutate(name.trim());
  };

  return (
    <>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground text-pretty">
        {step === 'agency'
          ? 'First, name your agency. It groups the client workspaces your team manages.'
          : 'Now add one workspace per client brand: its accounts, Brand Kit and calendar.'}
      </p>
      <p className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Step {step === 'agency' ? 1 : 2} of 2
      </p>
      <form
        className="mt-2 flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label htmlFor="first-run-name" className="sr-only">
          {step === 'agency' ? 'Agency name' : 'Workspace name'}
        </label>
        <input
          id="first-run-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={step === 'agency' ? 'Agency name' : 'Client or brand name'}
          className="h-9 w-full min-w-0 shrink-0 sm:flex-1 rounded-full bg-input px-4 text-sm text-foreground outline-none ring-1 ring-inset ring-border transition-shadow duration-fast placeholder:text-muted-foreground focus:ring-2 focus:ring-accent"
        />
        <Button type="submit" variant="primary" size="md" loading={create.isPending} disabled={!name.trim()}>
          {step === 'agency' ? 'Create agency' : 'Create workspace'}
        </Button>
      </form>
      {create.isError && (
        <p className="mt-2 text-xs text-destructive">
          {create.error instanceof ApiError ? create.error.message : 'Something went wrong. Please try again.'}
        </p>
      )}
    </>
  );
}
